// Notificaciones diarias de la app de celular (Android):
//   - 7:00 a. m.  "Mindful moment": una frase corta de calma en ingles.
//   - 6:00 p. m.  "Mini news": un dato curioso en ingles + una palabra con su
//                 significado en espanol.
//
// Como funciona: al abrir la app se programan las notificaciones de los
// proximos 14 dias (cada dia con un texto distinto, tomado de los bancos de
// abajo). Cada vez que el estudiante abre la app se vuelven a programar, asi
// que siempre quedan dias por delante. No necesita internet ni servidor.
//
// En la pagina web (navegador) no hace nada: solo funciona en la app.
//
// Para agregar mas textos, basta con sumar lineas a MINDFULNESS o MINI_NEWS
// (los textos nuevos entran en la rotacion automaticamente).

import { Capacitor } from '@capacitor/core'
import { LocalNotifications } from '@capacitor/local-notifications'

const CANAL = 'daily-english'
const DIAS_A_PROGRAMAR = 14
const HORA_MINDFULNESS = { h: 7, m: 0 }
const HORA_NOTICIA = { h: 18, m: 0 }
const CLAVE_APAGADO = 'eb_notificaciones_apagadas'
// Nuestros ids tienen 9 digitos (aaaammdd + 1 digito); asi no tocamos
// notificaciones de otras partes de la app, si las hubiera.
const ID_MINIMO_NUESTRO = 100000000

const MINDFULNESS = [
  'Take three slow breaths. In through your nose, out through your mouth. You are doing great.',
  'Relax your shoulders. Unclench your jaw. Breathe slowly for one minute.',
  'Name five things you can see right now. Notice their colors.',
  'Today I am learning, and that is enough. Be kind to yourself.',
  'Close your eyes for 30 seconds. Listen to the sounds around you.',
  'Breathe in for 4 seconds. Hold for 4. Breathe out for 6.',
  'Mistakes are proof that you are trying. Keep going.',
  'Put a hand on your heart and say: "I am calm and I am ready."',
  'Stretch your arms up high. Take a deep breath. Let it go.',
  'Think of one thing you are grateful for today.',
  'Drink some water slowly. Notice how it feels.',
  'You do not need to be perfect. You only need to practice.',
  'Feel your feet on the floor. You are here. You are safe.',
  'Let go of yesterday. Today is a new page.',
  'Smile gently. Even a small smile can change your mood.',
  'Walk slowly for two minutes. Feel every step.',
  'One thing at a time. Right now, just breathe.',
  'Say something kind to yourself in English today.',
  'Watch your thoughts like clouds in the sky. Let them pass.',
  'Take a five-minute break from your phone. Look out of a window.',
  'Progress is slow, but it is real. Be proud of your effort.',
  'Breathe in calm. Breathe out stress. Repeat three times.',
  'Think of someone you love and send them a good thought.',
  'Your best is enough for today.',
  'Listen to your favorite song. Close your eyes and enjoy it.',
  'Notice how your body feels. Where is the tension? Let it soften.',
  'Feeling nervous when you speak a new language is normal. Courage is speaking anyway.',
  'Every small step counts. Even ten minutes of English is a win.',
  'Before you sleep, remember one good thing from today.',
  'Pause. Breathe. Begin again.',
]

// Datos curiosos cortos y verdaderos, con una palabra de vocabulario.
const MINI_NEWS = [
  'Honey can last thousands of years without spoiling. Word: spoil = echarse a perder.',
  'An octopus has three hearts. Word: heart = corazón.',
  'A day on Venus is longer than a year on Venus. Word: planet = planeta.',
  'Bananas are berries, but strawberries are not. Word: berry = baya.',
  'Colombia has coasts on both the Pacific Ocean and the Caribbean Sea. Word: coast = costa.',
  'Colombia is one of the biggest coffee producers in the world. Word: harvest = cosecha.',
  'Bogotá is about 2,600 meters above sea level. Word: altitude = altitud.',
  'Your brain uses about 20% of your body\'s energy. Word: energy = energía.',
  'Sharks lived on Earth before trees did. Word: ancient = antiguo.',
  'Sound travels faster in water than in air. Word: faster = más rápido.',
  'A rainbow is really a full circle. From the ground, we usually see only half. Word: rainbow = arcoíris.',
  'Water boils at a lower temperature when you are high up in the mountains. Word: boil = hervir.',
  'Lightning can be hotter than the surface of the sun. Word: lightning = rayo.',
  'Dolphins sleep with half of their brain awake. Word: sleep = dormir.',
  'Reading in English for ten minutes a day helps your vocabulary grow. Word: improve = mejorar.',
  'Ants can lift many times their own body weight. Word: lift = levantar.',
  'Your heart beats about 100,000 times every day. Word: beat = latir.',
  'More than a billion people speak English around the world. Word: billion = mil millones.',
  'Spanish has nearly 500 million native speakers. Word: native = nativo.',
  'Penguins cannot fly, but they are excellent swimmers. Word: swim = nadar.',
  'A small cloud can weigh hundreds of tons. Word: weigh = pesar.',
  'Elephants can recognize themselves in a mirror. Word: mirror = espejo.',
  'The Amazon is the largest river in the world by volume of water. Word: river = río.',
  'Coffee beans are really seeds. Word: seed = semilla.',
  'People blink about 15 to 20 times every minute. Word: blink = parpadear.',
  'Reviewing new words before sleep can help your memory. Word: memory = memoria.',
  'The Pacific is the largest and deepest ocean on Earth. Word: deep = profundo.',
  'Butterflies can taste with their feet. Word: taste = saborear.',
  'A 20-minute walk every day is good for your body and your mood. Word: mood = ánimo.',
  'Crows are very smart birds. They can use tools to get food. Word: tool = herramienta.',
]

// Numero de dia (fecha local) para rotar los textos: el mismo dia siempre
// da el mismo texto, y cada dia cambia al siguiente.
function numeroDeDia(fecha) {
  return Math.floor(Date.UTC(fecha.getFullYear(), fecha.getMonth(), fecha.getDate()) / 86400000)
}

function idDeNotificacion(fecha, ranura) {
  const mm = String(fecha.getMonth() + 1).padStart(2, '0')
  const dd = String(fecha.getDate()).padStart(2, '0')
  return parseInt(`${fecha.getFullYear()}${mm}${dd}${ranura}`, 10)
}

function armarLista(ahora) {
  const lista = []
  for (let i = 0; i < DIAS_A_PROGRAMAR; i++) {
    const dia = new Date(ahora.getFullYear(), ahora.getMonth(), ahora.getDate() + i)
    const n = numeroDeDia(dia)

    const horaCalma = new Date(dia.getFullYear(), dia.getMonth(), dia.getDate(), HORA_MINDFULNESS.h, HORA_MINDFULNESS.m, 0)
    if (horaCalma > ahora) {
      const texto = MINDFULNESS[n % MINDFULNESS.length]
      lista.push({
        id: idDeNotificacion(dia, 1),
        title: 'Mindful moment 🌿',
        body: texto,
        largeBody: texto,
        channelId: CANAL,
        schedule: { at: horaCalma, allowWhileIdle: true },
        isExactNotification: false,
      })
    }

    const horaNoticia = new Date(dia.getFullYear(), dia.getMonth(), dia.getDate(), HORA_NOTICIA.h, HORA_NOTICIA.m, 0)
    if (horaNoticia > ahora) {
      const texto = MINI_NEWS[(n + 7) % MINI_NEWS.length]
      lista.push({
        id: idDeNotificacion(dia, 2),
        title: 'Mini news 📰',
        body: texto,
        largeBody: texto,
        channelId: CANAL,
        schedule: { at: horaNoticia, allowWhileIdle: true },
        isExactNotification: false,
      })
    }
  }
  return lista
}

async function cancelarNuestras() {
  const { notifications } = await LocalNotifications.getPending()
  const nuestras = notifications.filter(n => n.id >= ID_MINIMO_NUESTRO)
  if (nuestras.length > 0) {
    await LocalNotifications.cancel({ notifications: nuestras.map(n => ({ id: n.id })) })
  }
}

// Llamar cuando el estudiante entra a la app (ya con sesion iniciada).
export async function programarNotificacionesDiarias() {
  if (!Capacitor.isNativePlatform()) return
  try {
    if (localStorage.getItem(CLAVE_APAGADO) === '1') return

    let permiso = await LocalNotifications.checkPermissions()
    if (permiso.display === 'prompt' || permiso.display === 'prompt-with-rationale') {
      permiso = await LocalNotifications.requestPermissions()
    }
    if (permiso.display !== 'granted') return

    await LocalNotifications.createChannel({
      id: CANAL,
      name: 'Daily English',
      description: 'Mindful moments and mini news in English',
      importance: 3,
      visibility: 1,
    })

    await cancelarNuestras()
    const lista = armarLista(new Date())
    if (lista.length > 0) await LocalNotifications.schedule({ notifications: lista })
  } catch (err) {
    // Las notificaciones nunca deben romper la app
    console.warn('No se pudieron programar las notificaciones:', err)
  }
}

// Para un futuro boton de ajustes ("Recibir recordatorios diarios")
export async function desactivarNotificacionesDiarias() {
  try {
    localStorage.setItem(CLAVE_APAGADO, '1')
    if (Capacitor.isNativePlatform()) await cancelarNuestras()
  } catch (err) { console.warn(err) }
}

export async function activarNotificacionesDiarias() {
  try { localStorage.removeItem(CLAVE_APAGADO) } catch (err) { /* sin storage */ }
  await programarNotificacionesDiarias()
}

// Solo para pruebas
export const _bancos = { MINDFULNESS, MINI_NEWS, armarLista }
